import { router } from '@inertiajs/vue3';
import { gsap } from 'gsap/all';

/**
 * Per-page exit animation registry + Inertia navigation interceptor.
 *
 * Each page component registers an `exitAnim()` (the inverse of its mounted
 * intro). Before Inertia navigates away we cancel the visit, play that exit
 * animation, then re-issue the same visit once it finishes. The re-issued
 * visit is allowed straight through via the `bypass` flag.
 */

let currentExit = null;
let bypass = false;
let installed = false;
let originRect = null;

/**
 * @param {(() => unknown) | null} fn  Returns a GSAP tween/timeline, a Promise, or nothing.
 */
export function registerExit(fn) {
    currentExit = typeof fn === 'function' ? fn : null;
}

export function clearExit() {
    currentExit = null;
}

/**
 * Record the clicked element's viewport rect so the exit animation can zoom
 * from it, and the next page's mounted() can grow in from the same spot.
 *
 * @param {DOMRect} rect
 */
export function setOriginRect(rect) {
    originRect = rect;
}

/**
 * Read and clear the pending origin rect. Call once, on mount, at most.
 *
 * @returns {DOMRect | null}
 */
export function consumeOriginRect() {
    const rect = originRect;
    originRect = null;

    return rect;
}

/**
 * Resolve when the exit animation is done, whatever shape it is.
 *
 * @param {unknown} result
 * @returns {Promise<void>}
 */
function whenDone(result) {
    if (result && typeof result.then === 'function') {
        return Promise.resolve(result);
    }

    if (result && typeof result.eventCallback === 'function') {
        return new Promise((resolve) => {
            result.eventCallback('onComplete', resolve);
            // Safety net: never hang the app if the tween is killed mid-flight.
            gsap.delayedCall((result.duration?.() ?? 0.4) + 0.2, resolve);
        });
    }

    return Promise.resolve();
}

export function installPageTransition() {
    if (installed) {
        return;
    }
    installed = true;

    router.on('before', (event) => {
        if (bypass) {
            bypass = false;
            return true;
        }

        const visit = event.detail.visit;

        // Only intercept plain forward GET navigations that have an exit anim.
        if (!currentExit || visit.method !== 'get' || visit.preserveState) {
            return true;
        }

        event.preventDefault();
        const run = currentExit;

        whenDone(run()).then(() => {
            bypass = true;
            router.visit(visit.url, {
                method: 'get',
                data: visit.data,
                replace: visit.replace,
                preserveScroll: visit.preserveScroll,
                headers: visit.headers,
            });
        });

        return false;
    });
}
